import { createContext, ReactNode, useCallback, useState } from 'react';

interface SettingSaveContextType {
  hideSaveCard: () => void;
  isSaveCardLoading: boolean;
  isSaveCardVisible: boolean;
  onCancel: () => void;
  onSave: () => void;
  registerOnCancel: (callback: () => void) => void;
  registerOnSave: (callback: () => void) => void;
  setIsSaveCardLoading: (isLoading: boolean) => void;
  showSaveCard: () => void;
}

export const SettingSaveContext = createContext<SettingSaveContextType>({
  hideSaveCard: () => {},
  isSaveCardLoading: false,
  isSaveCardVisible: false,
  onCancel: () => {},
  onSave: () => {},
  registerOnCancel: () => {},
  registerOnSave: () => {},
  setIsSaveCardLoading: () => {},
  showSaveCard: () => {},
});

interface SettingSaveProviderProps {
  children: ReactNode;
}

export const SettingSaveProvider = ({ children }: SettingSaveProviderProps) => {
  const [isSaveCardLoading, setIsSaveCardLoading] = useState<boolean>(false);
  const [isSaveCardVisible, setIsSaveCardVisible] = useState<boolean>(false);
  const [onSaveCallback, setOnSaveCallback] = useState<(() => void) | null>(
    null
  );
  const [onCancelCallback, setOnCancelCallback] = useState<(() => void) | null>(
    null
  );

  /**
   * @desc Cancel action
   */
  const onCancel = useCallback(() => {
    if (onCancelCallback) {
      onCancelCallback();
    }
  }, [onCancelCallback]);

  /**
   * @desc Save action
   */
  const onSave = useCallback(() => {
    if (onSaveCallback) {
      onSaveCallback();
    }
  }, [onSaveCallback]);

  /**
   * @desc Show save card
   */
  const showSaveCard = useCallback(() => {
    setIsSaveCardVisible(true);
  }, []);

  /**
   * @desc Hide save card
   */
  const hideSaveCard = useCallback(() => {
    setIsSaveCardVisible(false);
  }, []);

  /**
   * @desc Register on save
   * @param callback
   */
  const registerOnSave = useCallback((callback: () => void) => {
    setOnSaveCallback(() => callback);
  }, []);

  /**
   * @desc Register on cancel
   * @param callback
   */
  const registerOnCancel = useCallback((callback: () => void) => {
    setOnCancelCallback(() => callback);
  }, []);

  return (
    <SettingSaveContext.Provider
      value={{
        hideSaveCard,
        isSaveCardLoading,
        isSaveCardVisible,
        onCancel,
        onSave,
        registerOnCancel,
        registerOnSave,
        setIsSaveCardLoading,
        showSaveCard,
      }}
    >
      {children}
    </SettingSaveContext.Provider>
  );
};
